import { z } from "zod";
import type { CustomerInfo, Service } from "@/types/appointment";
import type { ResolvedForfait } from "@/lib/forfaitBooking";
import type { AnyLocationChoice, LocationMode } from "@/pages/client/book/anyLocation/types";

// Défauts en CONSTANTES de module : un objet neuf à chaque lecture relancerait
// les effets qui en dépendent (auto-sauvegarde du contexte).
export const DEFAULT_LOCATION_MODE: LocationMode = { kind: "specific" };
export const DEFAULT_ANY_CHOICE: AnyLocationChoice = { groupIds: [], retained: null };

/**
 * Fonction pure de restauration du tunnel `/book`. Ni React ni `window` ici :
 * tout arrive par `snapshot` (produit par `readTunnelSnapshot`) et tout ressort
 * en données (consommé par `BookingContext`, tâche 2 du lot 8).
 */

// Minimal Hairdresser interface, dupliquée de `BookingContext.tsx` plutôt
// qu'importée : `BookingContext.tsx` importera `restoreTunnelState` d'ici
// (tâche 2/3 du lot 8), un import inverse créerait un cycle. Même forme que
// `BookingHairdresser` exporté de `BookingContext.tsx`.
export interface BookingHairdresser {
  id: string;
  name: string;
  salon_id: string;
  level?: number | null;
  image_url?: string | null;
  quote?: string | null;
  about_me?: string | null;
  portfolio_images?: string[] | null;
  interests?: string[] | null;
  specializations?: Record<string, boolean>;
  opening_hours?: Record<string, { open: boolean; start: string; end: string } | null>;
}

// `RecurringConfig` n'est même pas exporté de `BookingContext.tsx` (interface
// privée du fichier) : dupliquée ici pour la même raison que ci-dessus.
export interface RecurringConfig {
  intervalType: "weekly" | "monthly" | "custom";
  intervalValue: number;
  startDate: Date;
  endDate: Date;
}

const EXPIRY_HOURS = 24;
const SESSION_EXPIRY_MINUTES = 30;

const BOOKING_STORAGE_KEY = "flowcut_booking_data" as const;
const BOOKING_MODE_KEY = "flowcut_booking_mode" as const;
const SENSITIVE_DATA_KEY = "flowcut_sensitive_data" as const;

// --- Schémas Zod, recopiés de BookingContext.tsx:17-117 -------------------

const ServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  duration: z.number().nullable(),
  price: z.number().nullable(),
  description: z.string().optional(),
  section: z.string().nullable().optional(),
  subsection_id: z.string().nullable().optional(),
  bookable_online: z.boolean().optional(),
  price_type: z.enum(["fixed", "variable", "starting_from"]).optional(),
  duration_type: z.enum(["fixed", "variable", "starting_from"]).optional(),
}).passthrough();

const BookingModeSchema = z.object({
  isDirectBooking: z.boolean(),
  salonId: z.string().nullable(),
  bookingSource: z.enum(["direct_link"]).nullable(),
  timestamp: z.number(),
});

// Permissif : accepte un BookingHairdresser ou le pseudo-employé « any »
// (id: 'any'), qui peut ne pas porter salon_id.
const ServiceEmployeeSchema = z.object({
  id: z.string(),
  name: z.string(),
}).passthrough();

const ForfaitLineSchema = z.object({
  kind: z.enum(["service", "pause"]),
  forfaitItemId: z.string().optional(),
  serviceId: z.string().optional(),
  serviceName: z.string().optional(),
  hairdresserId: z.string().nullable().optional(),
  duration: z.number(),
  priceOverride: z.number().nullable().optional(),
});

const StoredForfaitSchema = z.object({
  forfaitId: z.string(),
  name: z.string(),
  lines: z.array(ForfaitLineSchema),
});

// Recopié de BookingContext.tsx:78-107, MOINS `hairdresser`, `serviceEmployees`
// et `currentStep` (règles de la tâche 1). Un `z.object` sans `.passthrough()`
// ignore silencieusement les clés inconnues : un ancien stockage qui porte
// encore ces trois champs se lit quand même, ils sont simplement stripés.
const TunnelBookingDataSchema = z.object({
  salonId: z.string().nullable(),
  locationId: z.string().nullable().optional(),
  service: ServiceSchema.nullable().optional(),
  services: z.array(ServiceSchema).optional(),
  serviceEmployeeIntent: z.record(z.string(), ServiceEmployeeSchema).optional(),
  sameEmployeeForAll: z.boolean().optional(),
  selectedForfait: StoredForfaitSchema.nullable().optional().catch(null),
  date: z.string().nullable().optional(),
  time: z.string().nullable().optional(),
  bookingSource: z.enum(["direct_link"]).nullable().optional(),
  isDirectBooking: z.boolean().optional(),
  recurringConfig: z.object({
    intervalType: z.enum(["weekly", "monthly", "custom"]),
    intervalValue: z.number(),
    startDate: z.string(),
    endDate: z.string(),
  }).nullable().optional(),
  // Tâche 9 (lot 4). `.catch(undefined)` : une valeur abîmée ne doit PAS effacer
  // tout le tunnel (échec zod = `wiped_invalid`), elle retombe sur le défaut.
  locationMode: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("specific") }),
    z.object({ kind: z.literal("any") }),
  ]).optional().catch(undefined),
  anyChoice: z.object({
    groupIds: z.array(z.string()),
    retained: z.object({
      locationId: z.string(),
      date: z.string(),
      time: z.string(),
      fallbackLocationIds: z.array(z.string()),
    }).nullable().catch(null),
  }).optional().catch(undefined),
  timestamp: z.number(),
});

const SensitiveDataSchema = z.object({
  customerInfo: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    notes: z.string().optional(),
  }),
  ownerUserId: z.string().nullable().optional(),
  timestamp: z.number(),
});

// --- Types produits ---------------------------------------------------------

export type TunnelSnapshot = {
  now: number;
  routeSalonId: string | null;
  isReschedule: boolean;
  bookingDataRaw: string | null;
  bookingModeRaw: string | null;
  contactRaw: string | null;
};

export type TunnelOutcome =
  | "restored" | "empty" | "wiped_other_salon" | "wiped_expired" | "wiped_invalid";

export type TunnelStorageKey =
  | "flowcut_booking_data" | "flowcut_booking_mode" | "flowcut_sensitive_data";

export type TunnelRestoration = {
  outcome: TunnelOutcome;
  mode: { isDirectBooking: boolean; salonId: string | null; bookingSource: "direct_link" | null } | null;
  tunnel: {
    salonId: string | null;
    locationId: string | null;
    services: Service[];
    serviceEmployeeIntent: Record<string, BookingHairdresser>;
    sameEmployeeForAll: boolean;
    selectedForfait: ResolvedForfait | null;
    date: Date | null;
    time: string | null;
    recurringConfig: RecurringConfig | null;
    locationMode: LocationMode;
    anyChoice: AnyLocationChoice;
  } | null;
  contact: { info: CustomerInfo; ownerUserId: string | null } | null;
  keysToRemove: TunnelStorageKey[];
};

// --- Fonction pure -----------------------------------------------------------

/** `JSON.parse` sûr. Rend `undefined` si le texte est illisible. */
function tryParse(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

function extractRawSalonId(parsed: unknown): string | null {
  if (parsed && typeof parsed === "object" && "salonId" in parsed) {
    const value = (parsed as { salonId?: unknown }).salonId;
    return typeof value === "string" && value.length > 0 ? value : null;
  }
  return null;
}

export function restoreTunnelState(snapshot: TunnelSnapshot): TunnelRestoration {
  // --- Salon : l'URL gagne. Vérifié AVANT toute validation Zod, sur un
  // parse tolérant (mode d'abord, puis données), comme le code actuel. ---
  const rawModeForSalonCheck = snapshot.bookingModeRaw !== null ? tryParse(snapshot.bookingModeRaw) : null;
  const rawDataForSalonCheck = snapshot.bookingDataRaw !== null ? tryParse(snapshot.bookingDataRaw) : null;
  const storedSalonId =
    extractRawSalonId(rawModeForSalonCheck) ?? extractRawSalonId(rawDataForSalonCheck);

  if (snapshot.routeSalonId && storedSalonId && storedSalonId !== snapshot.routeSalonId) {
    return {
      outcome: "wiped_other_salon",
      mode: null,
      tunnel: null,
      contact: null,
      keysToRemove: [BOOKING_STORAGE_KEY, BOOKING_MODE_KEY, SENSITIVE_DATA_KEY],
    };
  }

  const keysToRemove: TunnelStorageKey[] = [];

  // --- Mode (réservation directe). Expiration indépendante, 24 h. ---
  let mode: TunnelRestoration["mode"] = null;
  if (snapshot.bookingModeRaw !== null) {
    const rawMode = tryParse(snapshot.bookingModeRaw);
    const modeResult = rawMode !== undefined ? BookingModeSchema.safeParse(rawMode) : null;
    if (!modeResult || !modeResult.success) {
      keysToRemove.push(BOOKING_MODE_KEY);
    } else {
      const hoursSinceCreation = (snapshot.now - modeResult.data.timestamp) / (1000 * 60 * 60);
      if (hoursSinceCreation > EXPIRY_HOURS) {
        keysToRemove.push(BOOKING_MODE_KEY);
      } else {
        mode = {
          isDirectBooking: modeResult.data.isDirectBooking,
          salonId: modeResult.data.salonId,
          bookingSource: modeResult.data.bookingSource,
        };
      }
    }
  }

  // --- Panier (tunnel). ---
  let outcome: TunnelOutcome;
  let tunnel: TunnelRestoration["tunnel"] = null;
  let forceClearContact = false;

  if (snapshot.bookingDataRaw === null) {
    outcome = "empty";
  } else {
    const rawData = tryParse(snapshot.bookingDataRaw);
    const dataResult = rawData !== undefined ? TunnelBookingDataSchema.safeParse(rawData) : null;

    if (!dataResult || !dataResult.success) {
      outcome = "wiped_invalid";
      keysToRemove.push(BOOKING_STORAGE_KEY, SENSITIVE_DATA_KEY);
      forceClearContact = true;
    } else {
      const bookingData = dataResult.data;
      const hoursSinceCreation = (snapshot.now - bookingData.timestamp) / (1000 * 60 * 60);

      if (hoursSinceCreation > EXPIRY_HOURS) {
        outcome = "wiped_expired";
        keysToRemove.push(BOOKING_STORAGE_KEY, SENSITIVE_DATA_KEY);
        forceClearContact = true;
      } else {
        outcome = "restored";

        // Panier : `services` d'abord, repli sur `service` seul.
        const restoredServices: Service[] =
          bookingData.services && bookingData.services.length > 0
            ? (bookingData.services as Service[])
            : bookingData.service
              ? [bookingData.service as Service]
              : [];
        const restoredIds = new Set(restoredServices.map((s) => s.id));

        // Forfait : restauré seulement si chacune de ses lignes `service` a
        // son `serviceId` dans le panier restauré.
        const storedForfait = bookingData.selectedForfait ?? null;
        const forfaitIntact =
          !!storedForfait &&
          storedForfait.lines
            .filter((l) => l.kind === "service")
            .every((l) => !!l.serviceId && restoredIds.has(l.serviceId));

        // Intention : `serviceEmployeeIntent` seul, aucune migration ni
        // synthèse depuis un champ hérité.
        //
        // ⛔ Ronde de correction 1 : `ServiceEmployeeSchema` ne valide que
        // `id`/`name` (`.passthrough()` laisse le reste en `unknown`), et le
        // pseudo-employé « any » (`Book.tsx:1845`) n'écrit JAMAIS de
        // `salon_id` dans le stockage réel. Une conversion directe en
        // `Record<string, BookingHairdresser>` MENTIRAIT donc sur un champ
        // que TypeScript croirait garanti. Plutôt que de forcer cette
        // conversion (même via `unknown`), chaque entrée est reconstruite
        // avec un `salon_id` par défaut quand il manque : rien, dans ce
        // fichier ni dans `BookingContext.tsx`, ne lit jamais ce champ sur
        // une entrée de `serviceEmployeeIntent` (vérifié par recherche), donc
        // une chaîne vide y est sans effet, contrairement à un mensonge de
        // type qui resterait invisible jusqu'au jour où quelqu'un le lirait.
        const rawIntent = bookingData.serviceEmployeeIntent ?? {};
        const intent: Record<string, BookingHairdresser> = Object.fromEntries(
          Object.entries(rawIntent).map(([serviceId, value]) => [
            serviceId,
            {
              id: value.id,
              name: value.name,
              salon_id: typeof value.salon_id === "string" ? value.salon_id : "",
            },
          ]),
        );
        const hasRestoredIntent = Object.keys(intent).length > 0;

        tunnel = {
          salonId: bookingData.salonId,
          locationId: bookingData.locationId ?? null,
          services: restoredServices,
          serviceEmployeeIntent: intent,
          sameEmployeeForAll: hasRestoredIntent && bookingData.sameEmployeeForAll === true,
          selectedForfait: forfaitIntact ? (storedForfait as ResolvedForfait) : null,
          // E6 : un report ne doit pas semer une date/heure d'un tunnel abandonné.
          date: snapshot.isReschedule
            ? null
            : bookingData.date
              ? new Date(bookingData.date)
              : null,
          time: snapshot.isReschedule ? null : (bookingData.time ?? null),
          recurringConfig: bookingData.recurringConfig
            ? {
                ...bookingData.recurringConfig,
                startDate: new Date(bookingData.recurringConfig.startDate),
                endDate: new Date(bookingData.recurringConfig.endDate),
              }
            : null,
          // Champs absents (stockage d'avant le lot 4) = comportement d'aujourd'hui.
          // La PAIRE se restaure d'un bloc : jamais un mode any sans son choix,
          // jamais un choix hors du mode any. Un report repart toujours de zéro.
          ...(!snapshot.isReschedule && bookingData.locationMode?.kind === "any" && bookingData.anyChoice
            ? { locationMode: bookingData.locationMode, anyChoice: bookingData.anyChoice }
            : { locationMode: DEFAULT_LOCATION_MODE, anyChoice: DEFAULT_ANY_CHOICE }),
        };
      }
    }
  }

  // --- Contact. Expiration indépendante, 30 min. Un échec ici ne touche
  // jamais au tunnel. ---
  let contact: TunnelRestoration["contact"] = null;
  if (forceClearContact) {
    contact = null;
  } else if (snapshot.contactRaw !== null) {
    const rawContact = tryParse(snapshot.contactRaw);
    const contactResult = rawContact !== undefined ? SensitiveDataSchema.safeParse(rawContact) : null;

    if (!contactResult || !contactResult.success) {
      keysToRemove.push(SENSITIVE_DATA_KEY);
    } else {
      const minutesSinceCreation = (snapshot.now - contactResult.data.timestamp) / (1000 * 60);
      if (minutesSinceCreation > SESSION_EXPIRY_MINUTES) {
        keysToRemove.push(SENSITIVE_DATA_KEY);
      } else {
        contact = {
          info: contactResult.data.customerInfo,
          ownerUserId: contactResult.data.ownerUserId ?? null,
        };
      }
    }
  }

  return { outcome, mode, tunnel, contact, keysToRemove };
}

// --- Lecture du stockage réel (React/window vivent uniquement ici) --------

const readKey = (store: Storage | undefined, key: string): string | null => {
  try { return store?.getItem(key) ?? null; } catch { return null; }
};

/** Salon de la route : /book/:id, sinon ?salon=, sinon ?salonId=. */
export function readRouteSalonId(): string | null {
  try {
    const m = window.location.pathname.match(
      /\/book\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i,
    );
    if (m?.[1]) return m[1];
    const q = new URLSearchParams(window.location.search);
    return q.get("salon") || q.get("salonId");
  } catch { return null; }
}

export function readTunnelSnapshot(
  options: { now?: number; routeSalonId?: string | null } = {},
): TunnelSnapshot {
  let isReschedule = false;
  try { isReschedule = new URLSearchParams(window.location.search).get("reschedule") === "true"; }
  catch { isReschedule = false; }
  return {
    now: options.now ?? Date.now(),
    routeSalonId: options.routeSalonId !== undefined ? options.routeSalonId : readRouteSalonId(),
    isReschedule,
    bookingDataRaw: readKey(globalThis.localStorage, BOOKING_STORAGE_KEY),
    bookingModeRaw: readKey(globalThis.localStorage, BOOKING_MODE_KEY),
    contactRaw: readKey(globalThis.sessionStorage, SENSITIVE_DATA_KEY),
  };
}

export function applyTunnelKeyRemovals(keys: readonly TunnelStorageKey[]): void {
  for (const key of keys) {
    try {
      if (key === SENSITIVE_DATA_KEY) sessionStorage.removeItem(key);
      else localStorage.removeItem(key);
    } catch { /* stockage indisponible : rien à retirer */ }
  }
}
